import { LogEntry } from "../types";

/**
 * Convert labels array to object
 */
function labelsToObject(labels: [string, string][]): Record<string, string> {
  const obj: Record<string, string> = {};
  for (const [key, value] of labels) {
    obj[key] = value;
  }
  return obj;
}

/**
 * Format log entries for download in the specified format
 */
export function formatLogsForDownload(
  logEntries: LogEntry[],
  format: "json" | "txt",
  commonLabels: string[],
  orderBy: "asc" | "desc"
): string {
  // Sort by timestamp
  const sortedEntries = [...logEntries].sort((a, b) => {
    return orderBy === "desc" ? b.timestamp - a.timestamp : a.timestamp - b.timestamp;
  });

  if (format === "json") {
    const jsonVersion = sortedEntries.map((logEntry) => ({
      line: logEntry.log,
      timestamp: String(logEntry.timestamp * 1000000), // Convert ms to ns
      fields: labelsToObject(logEntry.labels),
    }));
    return JSON.stringify(jsonVersion, null, 2);
  } else {
    // TXT format
    const commonLabelsObj = commonLabels.reduce(
      (acc, label) => {
        const [key, value] = label.split("=");
        acc[key] = value;
        return acc;
      },
      {} as Record<string, string>
    );

    let blob = "";
    blob += `Common Labels: ${JSON.stringify(commonLabelsObj)}\n`;
    blob += `Line limit: 1000 (${sortedEntries.length} returned)\n`;
    blob += "\n";

    sortedEntries.forEach((logEntry) => {
      const timestamp = new Date(logEntry.timestamp).toISOString();
      blob += `${timestamp} ${logEntry.log}\n`;
    });

    return blob;
  }
}

/**
 * Trigger a file download in the browser
 */
export function triggerDownload(
  content: string,
  filename: string,
  format: "json" | "txt"
): void {
  const mimeType = format === "json" ? "application/json" : "text/plain";
  const file = new Blob([content], { type: mimeType });
  const href = URL.createObjectURL(file);

  const a = document.createElement("a");
  a.style.display = "none";
  a.classList.add("cpln-temp-a");
  a.download = `${filename}.${format}`;
  a.href = href;
  a.click();

  // Cleanup
  URL.revokeObjectURL(href);
}

/**
 * Download log entries as a file
 */
export function downloadLogs(
  logEntries: LogEntry[],
  format: "json" | "txt",
  commonLabels: string[],
  orderBy: "asc" | "desc",
  filenamePrefix?: string
): void {
  const content = formatLogsForDownload(logEntries, format, commonLabels, orderBy);
  const prefix = filenamePrefix ? `${filenamePrefix}-` : "";
  const filename = `${prefix}logs-${new Date().toISOString()}`;
  triggerDownload(content, filename, format);
}

/**
 * Copy log entries to clipboard as text
 */
export async function copyLogsToClipboard(
  logEntries: LogEntry[],
  commonLabels: string[],
  orderBy: "asc" | "desc"
): Promise<void> {
  const content = formatLogsForDownload(logEntries, "txt", commonLabels, orderBy);
  await navigator.clipboard.writeText(content);
}
